import proj4 from 'proj4';
import apiConfig from '../config/api';

proj4.defs('EPSG:2039', '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs');
proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs');

const VISITOR_KEY = 'urbanix_visitor_id';
const SESSION_KEY = 'urbanix_session_id';
const SEARCH_COUNT_KEY = 'urbanix_search_count_session';
const ATTRIBUTION_KEY = 'urbanix_campaign_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CRITICAL_EVENTS = new Set([
  'search_completed',
  'results_viewed',
  'plan_detail_open',
  'permit_detail_open',
  'alerts_signup_submitted',
  'alerts_signup_failed',
  'smart_alert_signup',
  'project_start_submitted',
  'contact_submitted',
  'ai_request_succeeded',
  'ai_request_failed',
]);

function gtag() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...arguments);
  }
}

function fbq() {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...arguments);
  }
}

function makeSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSessionId() {
  if (typeof window === 'undefined') return makeSessionId();
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next = makeSessionId();
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return makeSessionId();
  }
}

function getVisitorId() {
  if (typeof window === 'undefined') return makeSessionId();
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY);
    if (existing) return existing;
    const next = makeSessionId();
    window.localStorage.setItem(VISITOR_KEY, next);
    return next;
  } catch {
    return makeSessionId();
  }
}

function incrementSearchCount() {
  if (typeof window === 'undefined') return 1;
  try {
    const current = Number(window.sessionStorage.getItem(SEARCH_COUNT_KEY) || '0');
    const next = current + 1;
    window.sessionStorage.setItem(SEARCH_COUNT_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

function sanitizeParams(params = {}) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  });
  return out;
}

function readStoredAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function getReferrerParams() {
  if (typeof document === 'undefined') return {};
  const referrer = document.referrer || '';
  if (!referrer) return { traffic_source: 'direct', traffic_medium: 'none' };
  try {
    const url = new URL(referrer);
    const currentHost = window.location.hostname;
    if (url.hostname === currentHost) return {};
    return {
      referrer,
      referrer_domain: url.hostname,
      traffic_source: url.hostname,
      traffic_medium: 'referral',
    };
  } catch {
    return { referrer };
  }
}

function captureAttributionFromUrl() {
  if (typeof window === 'undefined') return {};
  const current = {};
  const params = new URLSearchParams(window.location.search || '');
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) current[key] = value.trim();
  }
  const clickIdKeys = ['fbclid', 'gclid'];
  for (const key of clickIdKeys) {
    const value = params.get(key);
    if (value) current[key] = value.trim();
  }
  const stored = readStoredAttribution();
  if (Object.keys(current).length === 0) {
    if (Object.keys(stored).length > 0) return stored;
    const referrerAttribution = {
      ...getReferrerParams(),
      landing_page: window.location.pathname + window.location.search,
      captured_at: new Date().toISOString(),
    };
    try {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(referrerAttribution));
    } catch {
      // ignore storage failures
    }
    return referrerAttribution;
  }

  const attribution = {
    ...stored,
    ...getReferrerParams(),
    ...current,
    traffic_source: current.utm_source || stored.traffic_source,
    traffic_medium: current.utm_medium || stored.traffic_medium,
    traffic_campaign: current.utm_campaign || stored.traffic_campaign,
    landing_page: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString(),
  };
  try {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // ignore storage failures
  }
  return attribution;
}

function getAttributionParams() {
  return sanitizeParams(captureAttributionFromUrl());
}

function commonParams(params = {}) {
  return sanitizeParams({
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...getAttributionParams(),
    ...params,
  });
}

function postCriticalEvent(eventName, params) {
  const body = JSON.stringify({
    eventName,
    sessionId: params.session_id,
    pagePath: params.page_path,
    sourceSurface: params.source_surface,
    searchMode: params.search_mode,
    municipality: params.municipality,
    hasResults: params.has_results,
    resultsCount: params.results_count,
    eventTs: new Date().toISOString(),
    params,
  });

  const endpoint = `${apiConfig.apiBaseUrl}/api/events`;
  let isCrossOrigin = false;
  try {
    if (typeof window !== 'undefined') {
      const endpointOrigin = new URL(endpoint, window.location.origin).origin;
      isCrossOrigin = endpointOrigin !== window.location.origin;
    }
  } catch {
    isCrossOrigin = false;
  }
  try {
    // sendBeacon can behave as a credentialed request in browsers.
    // For cross-origin analytics endpoints (e.g. localhost:3000 -> localhost:3001),
    // skip beacon to avoid wildcard CORS + credentials warnings.
    if (!isCrossOrigin && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
  } catch {
    // fallback below
  }

  fetch(endpoint, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {});
}

function coordsToLatLng(x, y) {
  if (x > 3000000 && y > 3000000) {
    const [lng, lat] = proj4('EPSG:3857', 'EPSG:4326', [x, y]);
    return { lat, lng };
  }
  const [lng, lat] = proj4('EPSG:2039', 'EPSG:4326', [x, y]);
  return { lat, lng };
}

const AnalyticsService = {
  track(eventName, params = {}, opts = {}) {
    const payload = commonParams(params);
    gtag('event', eventName, payload);

    if (CRITICAL_EVENTS.has(eventName) || opts.critical) {
      postCriticalEvent(eventName, payload);
    }
  },

  trackAppLoaded() {
    this.track('app_loaded', {
      source_surface: 'app_boot',
      viewport_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
      viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    });
  },

  trackRouteView(pathname) {
    const pagePath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const pageLocation = typeof window !== 'undefined' ? window.location.href : undefined;
    const pageTitle = typeof document !== 'undefined' ? document.title : undefined;
    gtag('event', 'page_view', commonParams({
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
      source_surface: 'spa_route',
    }));
    this.track('route_view', { page_path: pagePath, page_location: pageLocation, page_title: pageTitle, source_surface: 'spa_route' });
  },

  trackSearchModeChanged(searchMode, meta = {}) {
    this.track('search_mode_changed', {
      search_mode: searchMode,
      source_surface: meta.sourceSurface || 'search_form',
    });
  },

  trackSearchStarted(searchTerm, { searchMode, sourceSurface = 'search_form' } = {}) {
    this.track('search_started', {
      search_term: searchTerm,
      search_mode: searchMode || 'address',
      source_surface: sourceSurface,
    });
  },

  trackSearchValidationFailed(meta = {}) {
    this.track('search_validation_failed', {
      search_mode: meta.searchMode,
      error_code: meta.errorCode,
      source_surface: meta.sourceSurface || 'search_form',
    });
  },

  trackSearchCompleted({
    address,
    searchMode,
    gush,
    helka,
    municipality,
    coordinates,
    resultsCount,
    hasResults,
    sourceSurface = 'search_flow',
  } = {}) {
    const searchCount = incrementSearchCount();
    const geo = coordinates?.x && coordinates?.y ? coordsToLatLng(coordinates.x, coordinates.y) : {};
    const payload = {
      address,
      search_mode: searchMode || 'address',
      gush,
      helka,
      municipality,
      has_results: Boolean(hasResults),
      results_count: Number.isFinite(Number(resultsCount)) ? Number(resultsCount) : undefined,
      lat: geo.lat || undefined,
      lng: geo.lng || undefined,
      search_count_session: searchCount,
      source_surface: sourceSurface,
    };

    this.track('search_completed', payload, { critical: true });
  },

  trackResultsViewed(meta = {}) {
    this.track('results_viewed', {
      address: meta.address,
      search_mode: meta.searchMode,
      gush: meta.gush,
      helka: meta.helka,
      municipality: meta.municipality,
      has_results: meta.hasResults,
      results_count: meta.resultsCount,
      source_surface: meta.sourceSurface || 'results_page',
    }, { critical: true });
  },

  trackPlanDetailOpen(meta = {}) {
    this.track('plan_detail_open', {
      plan_number: meta.planNumber,
      title: meta.title,
      municipality: meta.municipality,
      source_surface: meta.sourceSurface || 'unknown',
    }, { critical: true });
  },

  trackPermitDetailOpen(meta = {}) {
    this.track('permit_detail_open', {
      permit_number: meta.permitNumber,
      request_number: meta.requestNumber,
      title: meta.title,
      municipality: meta.municipality,
      source_surface: meta.sourceSurface || 'unknown',
    }, { critical: true });
  },

  trackAlertsSignupStarted(meta = {}) {
    this.track('alerts_signup_started', {
      municipality: meta.municipality,
      search_mode: meta.searchMode,
      source_surface: meta.sourceSurface || 'results_page',
    });
  },

  trackAlertsSignupFailed(meta = {}) {
    this.track('alerts_signup_failed', {
      municipality: meta.municipality,
      search_mode: meta.searchMode,
      source_surface: meta.sourceSurface || 'results_page',
      error_code: meta.errorCode,
      error_message: meta.errorMessage,
    }, { critical: true });
  },

  trackAlertsSignupSubmitted(meta = {}) {
    const payload = {
      municipality: meta.municipality,
      search_mode: meta.searchMode,
      source_surface: meta.sourceSurface || 'results_page',
      has_results: meta.hasResults,
      results_count: meta.resultsCount,
    };
    this.track('alerts_signup_submitted', payload, { critical: true });
    this.track('smart_alert_signup', payload, { critical: true });
    fbq('track', 'CompleteRegistration', {
      content_name: 'Smart Alert Signup',
      status: 'submitted',
      ...getAttributionParams(),
      municipality: meta.municipality,
      search_mode: meta.searchMode,
    });
  },

  getAttributionParams,

  trackProjectStartCtaClick(meta = {}) {
    this.track('project_start_cta_click', {
      source_surface: meta.sourceSurface || 'project_start_form',
      municipality: meta.municipality,
    });
  },

  trackProjectStartSubmitted(meta = {}) {
    this.track('project_start_submitted', {
      source_surface: meta.sourceSurface || 'project_start_form',
      municipality: meta.municipality,
      address: meta.address,
    }, { critical: true });
  },

  trackContactSubmitted(meta = {}) {
    this.track('contact_submitted', {
      source_surface: meta.sourceSurface || 'contact_page',
      topic: meta.topic,
      address: meta.address,
    }, { critical: true });
  },

  trackAIRequestStarted(meta = {}) {
    this.track('ai_request_started', {
      address: meta.address,
      source_surface: meta.sourceSurface || 'feasibility',
      municipality: meta.municipality,
    });
  },

  trackAIRequestSucceeded(meta = {}) {
    this.track('ai_request_succeeded', {
      address: meta.address,
      source_surface: meta.sourceSurface || 'feasibility',
      municipality: meta.municipality,
    }, { critical: true });
  },

  trackAIRequestFailed(meta = {}) {
    this.track('ai_request_failed', {
      address: meta.address,
      source_surface: meta.sourceSurface || 'feasibility',
      municipality: meta.municipality,
      error_code: meta.errorCode,
    }, { critical: true });
  },

  trackTabChange(tab) {
    this.track('tab_change', { tab_name: tab, source_surface: 'results_tabs' });
  },

  trackPDFDownload(address) {
    this.track('pdf_download', {
      address: address || undefined,
      source_surface: 'ai_recommendation',
    });
  },

  trackShareResults(meta = {}) {
    this.track('share_results_clicked', {
      address: meta.address,
      search_mode: meta.searchMode,
      municipality: meta.municipality,
      source_surface: meta.sourceSurface || 'results_header',
    });
  },

  // Backward-compatible wrappers
  trackSearch(address, meta = {}) {
    this.trackSearchCompleted({
      address,
      searchMode: meta.searchMode,
      gush: meta.gush,
      helka: meta.helka,
      municipality: meta.municipality,
      coordinates: meta.coordinates,
      hasResults: true,
    });
  },

  trackAIRecommendation(address) {
    this.trackAIRequestStarted({ address, sourceSurface: 'legacy' });
  },

  trackContactForm(source, address) {
    this.trackContactSubmitted({ sourceSurface: source || 'legacy', address });
  },
};

export default AnalyticsService;
